/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Import all the third party stuff
import Analytics from '@opencare/analytics';
import {
  ErrorBoundary as RollbarErrorBoundary,
  Provider as RollbarProvider,
} from '@rollbar/react';
import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';

import 'sanitize.css/sanitize.css';
import { PersistGate } from 'redux-persist/integration/react';

import { BRAND_COLORS } from 'brand-guidelines/colors';

// Import root app
import App from 'containers/App';

// Import antd styles so we can override them with our own in app.css
import 'opencare-crown';

// Base styles
import './app.css';

import ConnectedLanguageProvider from 'containers/LanguageProvider';
import ToothySad from 'pages/common/components/svgs/ToothySad';
import WindowDimensionsProvider from 'providers/WindowDimensionsProvider';
import history from 'utils/history';

// Load the favicon and the .htaccess file
// eslint-disable-next-line import/no-unresolved
import '!file-loader?name=[name].[ext]!./assets/favicon.png';
import 'file-loader?name=.htaccess!./.htaccess';

import { webWidget } from 'utils/zendesk';

import CONFIG from '../app.config';

import configureStore from './configureStore';

// Import i18n messages
import { translationMessages } from './i18n';
import Logger from './utils/Logger';
import { StorageSetup } from './utils/storage';

const rollbarInstance = Logger.initialize();

// Setup localforage
StorageSetup();

// Create redux store with history
const initialState = {};
// The persistor is a separate package that allows the redux store to maintain it's state on refresh of the page by saving data in local storage. We're currently using this to stop the redux store from being wiped on refresh of the page as we won't have the matchToken or matchId in the url params anymore
const { store, persistor } = configureStore(initialState, history);

const MOUNT_NODE = document.getElementById('app');
const root = createRoot(MOUNT_NODE);

// Initialize Opencare Analytics library
const analytics = new Analytics();
analytics.initialize({
  appId: `search-v2:${CONFIG.ENV}`,
  environment: CONFIG.ENV,
  ocApiUrl: CONFIG.API_URL,
  pixels: {
    // These pixels are enabled by default
    // but we'll explicitly enable them here for clarity
    [analytics.pixelNames.FULLSTORY]: true,
    [analytics.pixelNames.HEAP]: true,
    [analytics.pixelNames.SNOWPLOW]: true,
    [analytics.pixelNames.OPENCARE]: true,

    // These pixels are disabled by default
    [analytics.pixelNames.GOOGLE_ANALYTICS]: true,
    [analytics.pixelNames.GOOGLE_TAGS]: true,
    [analytics.pixelNames.BING]: true,
    [analytics.pixelNames.STATSIG]: true,
  },
});

/**
 * This component is used to display a nice fallback UI when an error occurs.
 *
 * @param {{ error: Error | null; }} param0
 */
const ErrorFallbackUI = ({ error }) => { // eslint-disable-line react/prop-types
  console.error('Error fallback used!', error);

  const buttonStyles = {
    display: 'flex',
    alignItems: 'center',
    marginTop: '20px',
    padding: '10px 20px',
    borderRadius: '16px',
    backgroundColor: BRAND_COLORS.darkGreen,
    color: BRAND_COLORS.white,
    fontWeight: '600',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100vw',
      height: '100vh',
      textAlign: 'center',
      padding: '20px',
    }}
    >
      <ToothySad height="100px" />
      <h3 style={{ marginTop: '20px' }}>Sorry, something went wrong.</h3>

      <div>
        <p>Please refresh the page and try again.</p>
        <p>If the problem persists, please contact support.</p>
        <p>In the meantime, we have been notified of the issue and are looking into it.</p>
      </div>

      <button
        type="button"
        style={{
          ...buttonStyles,
          border: 0,
        }}
        onClick={() => window.location.reload()}
      >
        Refresh Page
      </button>

      <a
        href="/"
        style={buttonStyles}
      >
        Go to home page
      </a>
    </div>
  );
};

// Component
const render = (messages) => {
  root.render(
    <RollbarProvider instance={rollbarInstance}>
      <RollbarErrorBoundary fallbackUI={ErrorFallbackUI}>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ConnectedLanguageProvider messages={messages}>
              <ConnectedRouter history={history}>
                <WindowDimensionsProvider>
                  <App />
                </WindowDimensionsProvider>
              </ConnectedRouter>
            </ConnectedLanguageProvider>
          </PersistGate>
        </ReduxProvider>
      </RollbarErrorBoundary>
    </RollbarProvider>,
  );
};

if (module.hot) {
  // Hot reloadable React components and translation json files
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept(['./i18n', 'containers/App'], () => {
    root.unmount();
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    .then(() => Promise.all([import('intl/locale-data/jsonp/en.js')]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}

/**
 * Ensure the Zendesk Web Widget is hidden when the page loads.
 */
if (webWidget.isReady()) {
  webWidget.hide();
}
